import api from './api';
import { transformPaginatedResponse } from './transformers';

import type {
  ContactZBootstrapPayload,
  ContactZConnection,
  ContactZIntelligencePersona,
  ContactZOfficialTemplate,
  ContactZIntegrationBinding,
  ContactZIntegrationOverviewResponse,
  ContactZManualBindPayload,
  ContactZManualBindResult,
} from '../types';

export interface ContactZWebhookTestResult {
  delivered?: boolean;
  event?: string;
  statusCode?: number;
  responseBody?: Record<string, unknown> | null;
}

export interface PaginatedContactZConnections {
  count: number;
  next: string | null;
  previous: string | null;
  results: ContactZConnection[];
}

/**
 * Load the operational bootstrap payload for Contact Z UI surfaces.
 */
export async function fetchContactZBootstrap(): Promise<ContactZBootstrapPayload> {
  const response = await api.get<ContactZBootstrapPayload>('/api/contact-z/bootstrap/');
  return response.data;
}

/**
 * Load the tenant-scoped Contact Z integration binding.
 */
export async function fetchContactZIntegration(): Promise<ContactZIntegrationBinding | null> {
  const response = await api.get<ContactZIntegrationOverviewResponse>('/api/contact-z/integration/');
  return response.data.integration;
}

/**
 * Bootstrap the tenant-scoped Contact Z integration on demand.
 */
export async function bootstrapContactZIntegration(): Promise<ContactZIntegrationBinding> {
  const response = await api.post<ContactZIntegrationOverviewResponse>('/api/contact-z/integration/bootstrap/', {});
  if (!response.data.integration) {
    throw new Error('Contact Z bootstrap did not return an integration binding.');
  }
  return response.data.integration;
}

/**
 * Attach the current tenant to one existing Contact Z integration and optionally import its channels.
 */
export async function manualBindContactZIntegration(
  payload: ContactZManualBindPayload,
): Promise<ContactZManualBindResult> {
  const response = await api.post<ContactZManualBindResult>('/api/contact-z/integration/manual-bind/', payload);
  return response.data;
}

/**
 * Ask Contact Z to emit a ping callback to the currently configured webhook.
 */
export async function testContactZWebhook(message: string): Promise<ContactZWebhookTestResult> {
  const response = await api.post<ContactZWebhookTestResult>('/api/contact-z/integration/test-webhook/', {
    message,
  });
  return response.data;
}

/**
 * List local Contact Z connections for the current tenant.
 */
export async function fetchContactZConnections(): Promise<ContactZConnection[]> {
  const response = await api.get('/api/contact-z/connections/');
  const data = response.data.results ? transformPaginatedResponse<ContactZConnection>(response.data) : null;
  if (data) {
    return data.results;
  }
  return Array.isArray(response.data) ? response.data : [];
}

/**
 * List Contact Z AI personas eligible for one local connection/channel.
 */
export async function fetchContactZIntelligencePersonas(
  connectionId: string,
): Promise<ContactZIntelligencePersona[]> {
  const response = await api.get<{ results?: ContactZIntelligencePersona[] } | ContactZIntelligencePersona[]>(
    `/api/contact-z/connections/${connectionId}/intelligence-personas/`,
  );
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return Array.isArray(response.data.results) ? response.data.results : [];
}

/** List approved official templates for one tenant-scoped Cloud connection. */
export async function fetchContactZOfficialTemplates(
  connectionId: string,
): Promise<ContactZOfficialTemplate[]> {
  const response = await api.get<{ results?: ContactZOfficialTemplate[] }>(
    `/api/contact-z/connections/${connectionId}/official-templates/`,
  );
  return Array.isArray(response.data.results) ? response.data.results : [];
}

/** Queue asynchronous synchronization of official templates in Contact Z. */
export async function syncContactZOfficialTemplates(
  connectionId: string,
): Promise<{ taskId: string }> {
  const response = await api.post<{ taskId: string }>(
    `/api/contact-z/connections/${connectionId}/sync-official-templates/`,
    {},
  );
  return response.data;
}

/**
 * Create a new local Contact Z connection and provision its remote channel.
 */
export async function createContactZConnection(name: string): Promise<ContactZConnection> {
  const response = await api.post<ContactZConnection>('/api/contact-z/connections/', { name });
  return response.data;
}

/**
 * Update local mutable fields of a Contact Z connection.
 */
export async function updateContactZConnection(id: string, name: string): Promise<ContactZConnection> {
  const response = await api.patch<ContactZConnection>(`/api/contact-z/connections/${id}/`, { name });
  return response.data;
}

/**
 * Remove a Contact Z connection and its remote channel.
 */
export async function deleteContactZConnection(id: string): Promise<void> {
  await api.delete(`/api/contact-z/connections/${id}/`);
}

/**
 * Load the current state for one Contact Z connection from the local API.
 */
export async function getContactZConnectionState(id: string): Promise<ContactZConnection> {
  const response = await api.get<ContactZConnection>(`/api/contact-z/connections/${id}/state/`);
  return response.data;
}

/**
 * Force a sync-state roundtrip through Contact Z.
 */
export async function syncContactZConnectionState(id: string): Promise<ContactZConnection> {
  const response = await api.post<ContactZConnection>(`/api/contact-z/connections/${id}/sync-state/`, {});
  return response.data;
}

/**
 * Generate or refresh a QR code for a Contact Z connection.
 */
export async function connectContactZConnection(id: string): Promise<ContactZConnection> {
  const response = await api.post<ContactZConnection>(`/api/contact-z/connections/${id}/connect/`, {});
  return response.data;
}

/**
 * Disconnect a Contact Z connection from the remote provider.
 */
export async function disconnectContactZConnection(id: string): Promise<ContactZConnection> {
  const response = await api.post<ContactZConnection>(`/api/contact-z/connections/${id}/disconnect/`, {});
  return response.data;
}

/**
 * Restart a Contact Z connection remotely.
 */
export async function restartContactZConnection(id: string): Promise<ContactZConnection> {
  const response = await api.post<ContactZConnection>(`/api/contact-z/connections/${id}/restart/`, {});
  return response.data;
}
